/**
 * Shared staff invoice-reminder outcome copy and delivery-status helpers.
 * Keep deal and portal reminder dialogs consistent.
 */

export const REMINDER_DELIVERY_POLL_MS = 4_000;
export const REMINDER_DELIVERY_POLL_MAX_MS = 2 * 60_000;

export type StaffReminderApiOutcome =
  | "sending"
  | "submitted"
  | "sent"
  | "delivered"
  | "read"
  | "failed"
  | "already_submitted"
  | "already_sent"
  | "skipped"
  | "in_flight"
  | "forbidden";

export type ReminderDeliveryPollState =
  | "pending"
  | "sent"
  | "delivered"
  | "read"
  | "failed";

export function staffReminderToastMessage(
  outcome: StaffReminderApiOutcome | string,
  options?: {
    channel?: "email" | "whatsapp" | string | null;
    reason?: string | null;
  },
): { kind: "success" | "message" | "error"; text: string } {
  const channel = String(options?.channel ?? "whatsapp").toLowerCase();
  const via =
    channel === "email" ? "email" : channel === "whatsapp" ? "WhatsApp" : channel;
  const reason = String(options?.reason ?? "").trim();

  switch (outcome) {
    case "submitted":
    case "pending_confirmation":
      return {
        kind: "message",
        text: `Reminder submitted via ${via} — confirming delivery.`,
      };
    case "already_submitted":
      return {
        kind: "message",
        text: "This reminder has already been submitted. Delivery confirmation is pending.",
      };
    case "sent":
      return {
        kind: "success",
        text: `Reminder sent via ${via}.`,
      };
    case "delivered":
      return {
        kind: "success",
        text: `Reminder delivered via ${via}.`,
      };
    case "read":
      return {
        kind: "success",
        text: "Reminder read on WhatsApp.",
      };
    case "already_sent":
      return {
        kind: "message",
        text: "Reminder was already sent. No duplicate was sent.",
      };
    case "skipped":
      return {
        kind: "message",
        text: reason ? `Reminder skipped: ${reason}` : "Reminder skipped.",
      };
    case "in_flight":
      return {
        kind: "message",
        text: "Reminder already in progress",
      };
    case "failed":
      return {
        kind: "error",
        text: reason ? `Reminder failed: ${reason}` : "Reminder failed.",
      };
    default:
      return {
        kind: "message",
        text: `Reminder outcome: ${outcome}`,
      };
  }
}

/** Never treat accepted/queued/submitted as a red failure. */
export function isNeutralPendingReminderOutcome(outcome: string | null | undefined): boolean {
  const o = String(outcome ?? "").trim().toLowerCase();
  return (
    o === "submitted" ||
    o === "pending_confirmation" ||
    o === "already_submitted" ||
    o === "accepted" ||
    o === "queued" ||
    o === "sending" ||
    o === "reconciled"
  );
}

export function classifyReminderDeliveryPoll(input: {
  status?: string | null;
  providerStatus?: string | null;
}): ReminderDeliveryPollState {
  const status = String(input.status ?? "").trim().toLowerCase();
  const provider = String(input.providerStatus ?? "").trim().toLowerCase();
  const combined = provider || status;

  if (combined === "read" || status === "read") return "read";
  if (combined === "delivered" || status === "delivered") return "delivered";
  if (
    combined === "failed" ||
    combined === "undelivered" ||
    combined === "canceled" ||
    combined === "cancelled" ||
    status === "failed" ||
    status === "undelivered"
  ) {
    return "failed";
  }
  if (combined === "sent" || status === "sent") return "sent";
  return "pending";
}
